import { normalizeStatus } from '../workitems/utils/workItemUtils';
import type { ActionPickerOption } from '../dropdown';

/**
 * Job status options for the status-pill ActionPicker. Each carries a `dvValue`
 * (the Dynamics title-case string the orchestrator expects) so the chosen
 * option maps back to the existing status-update API without a lookup table.
 * (Moved from the former StatusDropdown component.)
 */
interface StatusDef extends ActionPickerOption {
  dvValue: string;
}

const STATUS_OPTIONS: StatusDef[] = [
  { value: 'new',              label: 'New',                            colour: '#4A90D9', dvValue: 'New' },
  { value: 'diagnostics',      label: 'Ready for Diagnostics',          colour: '#9B59B6', dvValue: 'Ready for Diagnostics' },
  { value: 'in_progress',      label: 'In Progress',                    colour: '#F39C12', dvValue: 'In Progress' },
  { value: 'resolved',         label: 'Resolved',                       colour: '#6dcb6d', dvValue: 'Resolved' },
  { value: 'waiting_parts',    label: 'Waiting for Parts',              colour: '#E67E22', dvValue: 'Waiting for Parts' },
  { value: 'waiting_customer', label: 'Waiting on Customer',            colour: '#E74C3C', dvValue: 'Waiting on Customer' },
  { value: 'waiting_payment',  label: 'Waiting on Payment',             colour: '#B7950B', dvValue: 'Waiting on Payment' },
  { value: 'third_party',      label: 'With 3rd Party for Repairs',     colour: '#7F8C8D', dvValue: 'With 3rd Party for Repairs' },
  { value: 'waiting_supplier', label: 'Waiting on Supplier for Quote',  colour: '#EF9F27', dvValue: 'Waiting on Supplier for Quote' },
  { value: 'scheduled',        label: 'Scheduled',                      colour: '#8E44AD', dvValue: 'Scheduled' },
  { value: 'customer_reply',   label: 'Customer Reply',                 colour: '#2980B9', dvValue: 'Customer Reply' },
  { value: 'parts_to_order',   label: 'Parts to be Ordered',            colour: '#16A085', dvValue: 'Parts to be ordered' },
];

const DELETED_OPTION: StatusDef = {
  value: 'deleted',
  label: 'Deleted',
  colour: '#BDC3C7',
  dvValue: 'Deleted',
  destructive: true,
};

const ALL_OPTIONS: StatusDef[] = [...STATUS_OPTIONS, DELETED_OPTION];

/** Options passed to ActionPicker (Deleted renders after a divider, muted). */
export const STATUS_PICKER_OPTIONS: ActionPickerOption[] = ALL_OPTIONS;

/**
 * Map a raw work-item status (Dynamics title-case or kebab slug) into this
 * picker's value space, for highlighting the active option.
 */
export function toStatusDropdownValue(rawStatus: string | undefined): string {
  if (!rawStatus) return '';
  const slug = normalizeStatus(rawStatus);
  return ALL_OPTIONS.find((o) => normalizeStatus(o.dvValue) === slug)?.value ?? '';
}

/** Map a picker value back to the Dynamics status string sent to the API. */
export function statusDropdownDvValue(value: string): string | undefined {
  return ALL_OPTIONS.find((o) => o.value === value)?.dvValue;
}
